import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import InputAdornment from '@mui/material/InputAdornment';
import { 
    Close as CloseIcon, 
    Visibility as VisibilityIcon, 
    VisibilityOff as VisibilityOffIcon 
} from '@mui/icons-material';
import {
    TextInput,
    Text,
    Button,
    Theme,
    Services,
    Modal,
    Loader,
    Container,
    Form,
    Logo,
    i18n,
    tokens,
    NumberInput
} from '@simplybenefits/sb-ui-library';
import { isMobile } from 'react-device-detect';
import * as session from '../../services/session.js';
import { redirect, checkMobileRedirectToken, setMobileRedirectToken } from '../../services/helpers.js';
import * as notifier from '../../actions/notifier.js';
import { login, forgotPassword, submitMfaCode } from '../../actions/user.js';
import redirectImage from '../../assets/app-redirect.png';

class Login extends Component {
    state = {
        email: '',
        emailError: false,
        password: '',
        passwordError: false,
        forgotPassword: false,
        visibility: false,
        showDeviceWarning: false,
        language: '',
        screen: 'login',
        mfaSession: null,
        mfaCode: ''
    };

    componentDidMount() {
        // Set the locale to the user's language retrieved from local storage
        const userLanguage = session.getUserLanguage() || "en";
        i18n.setLocale(userLanguage);
        this.setState({ language: userLanguage });
        
        if (window.document.documentMode) {
            this.props.dispatch(notifier.displayBrowserMessage(i18n.t(tokens.labels.notifications.simplyBenefitsIsBestUsed)));
        }

        if (window.location.href.indexOf('invalid-token') > -1) {
            this.props.dispatch(notifier.displayError({
                verbiage: i18n.t(tokens.labels.notifications.yourSessionHasExpired)
            }));
        }

        if (window.location.href.indexOf('failed-whitelabel') > -1) {
            this.props.dispatch(notifier.displayError({
                verbiage: i18n.t(tokens.labels.notifications.youDoNotBelongToThisOrganization)
            }));
        }

        if (isMobile) {
            const showWarning = checkMobileRedirectToken();

            if (showWarning) {
                this.setState({
                    showDeviceWarning: true
                });
            }
        }
    }

    _removeRedirectWarning = () => {
        setMobileRedirectToken();

        this.setState({
            showDeviceWarning: false
        });
    };

    _setScreen = (screen = 'login', mfaSession = null) => {
        this.setState({
            screen,
            mfaSession,
            mfaCode: ''
        });
    };

    _triggerRedirect = () => {
        redirect();
    };

    _toggleVisibility = () => {
        this.setState({
            visibility: !this.state.visibility
        });
    };

    _toggleForgotPassword = () => {
        this.setState({
            forgotPassword: !this.state.forgotPassword
        });
    };

    _onBlur = async (type) => {
        this.setState({
            [`${ type }Error`]: await Services.validateInput(type, this.state[type])
        });
    };

    _handleInputChange = (type, event) => {
        this.setState({
            [type]: event.target.value.trim()
        });
    };

    _login = async () => {
        const emailError = await Services.validateInput('email', this.state.email);
        const passwordError = await Services.validateInput('password', this.state.password);

        this.setState({
            emailError,
            passwordError
        });

        if (emailError || passwordError) {
            return null;
        }

        const data = await this.props.dispatch(login(this.state.email, this.state.password, this.state.language));

        if (data && data.mfaSession) {
            this._setScreen('MFA', data.mfaSession);
        }

        return null;
    };

    _submitMFACode = async () => {
        const mfaCodeError = await Services.validateInput('checkEmpty', this.state.mfaCode);

        this.setState({
            mfaCodeError
        });

        if (mfaCodeError) {
            return null;
        }

        await this.props.dispatch(submitMfaCode(this.state.mfaCode, this.state.mfaSession));
        return null;
    };

    _forgotPassword = async () => {
        const emailError = await Services.validateInput('email', this.state.email);
        this.setState({ emailError });

        if (!emailError) {
            await this.props.dispatch(forgotPassword({ email: this.state.email }));
        }

        this._toggleForgotPassword();

        return null;
    };

    _onKeyPress = (event) => {
        if (event.key === 'Enter' && event.target.tagName !== 'BUTTON') {
            event.preventDefault();
            this._login();
        }
    };

    render() {
        const { whitelabel } = this.props;
        const { showDeviceWarning } = this.state;

        const styles = {
            redirectContainer: {
                display: 'flex',
                justifyContent: 'center',
                width: '100%'
            },
            redirect: {
                width: '93%',
                objectFit: 'contain',
                position: 'absolute',
                zIndex: 0
            },
            closeModalIcon: {
                cursor: 'pointer',
                display: 'block',
                right: '4%',
                top: '4%',
                width: '10%',
                height: '10%',
                position: 'absolute',
                color: Theme.colors.black,
                zIndex: 1
            },
            subtext: {
                color: Theme.colors.mainTextGrey,
                cursor: 'pointer',
                display: 'block',
                fontSize: '12px',
                marginBottom: '16px',
                marginTop: '16px',
                textAlign: 'center'
            }
        };

        if (this.state.screen === 'MFA') {
            return (
                <Container background={"true"}>
                    <Logo whitelabel={whitelabel} />

                    <Form customStyle={{
                        width: '450px',
                        paddingTop: 5,
                        paddingBottom: 10,
                        paddingRight: 40,
                        paddingLeft: 40
                    }}>
                        <Text
                            mediumLarge
                            bold
                            display="block"
                            align="left"
                            margin="20px 0"
                            customLinkStyle={{ lineHeight: '40px' }}
                        >
                            { i18n.t(tokens.titles.mfa) }
                        </Text>

                        <Text
                            medium
                            display="block"
                            align="left"
                            margin="20px 0 0 0"
                            customLinkStyle={{ lineHeight: '40px' }}
                        >
                            { i18n.t(tokens.labels.mfaLoginMessage) }
                        </Text>

                        <NumberInput
                            id="mfaCode"
                            label={ i18n.t(tokens.labels.code) }
                            onBlur={() => this._onBlur('mfaCode')}
                            onChange={(value) => this._handleInputChange('mfaCode', value)}
                            error={Boolean(this.state.mfaCodeError)}
                            helperText={this.state.mfaCodeError}
                            fullWidth
                            autofocus
                        />

                        <Button
                            onClick={ this._submitMFACode }
                            color="primary"
                            fullWidth
                            marginTop={ 3 }
                        >
                            { i18n.t(tokens.buttons.continue) }
                        </Button>

                        <span style={ styles.subtext }>
                            <Button
                                onClick={ this._setScreen }
                                transparent
                                customStyle={{ textTransform: "none", padding: 0, margin: 0, '&:hover': { backgroundColor: 'lightgrey' } }}
                            >
                                <Text
                                    small
                                    avMedium
                                    customStyle={{
                                        fontSize: "12px",
                                        letterSpacing: "0.34284px"
                                    }}>
                                    { i18n.t(tokens.buttons.backToLogin) }
                                </Text>

                            </Button>
                        </span>
                    </Form>

                    {this.props.loader?.isLoading && <Loader whitelabel={ whitelabel } />}
                </Container>
            );
        }

        return (
            <Container background={"true"}>
                <Logo whitelabel={whitelabel} />

                {
                    showDeviceWarning ?

                        <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>

                            <CloseIcon
                                sx={ styles.closeModalIcon }
                                onClick={ this._removeRedirectWarning }
                            />
                            <img
                                alt={ `Redirect Prompt` }
                                src={ redirectImage }
                                style={ styles.redirect }
                                onClick={ this._triggerRedirect }
                            />
                        </div>
                        :
                        <Form customStyle={{
                            width: '450px',
                            paddingTop: 5,
                            paddingBottom: 10,
                            paddingRight: 40,
                            paddingLeft: 40
                        }}>
                            <Text
                                mediumLarge
                                bold
                                display="block"
                                align="left"
                                margin="20px 0"
                                customLinkStyle={{ lineHeight: '40px' }}
                            >
                                { i18n.t(tokens.labels.dashboard.welcome) }
                            </Text>

                            <TextInput
                                id="login"
                                label={ i18n.t(tokens.labels.email) }
                                type="email"
                                value={this.state.email}
                                onBlur={() => this._onBlur('email')}
                                onChange={(value) => this._handleInputChange('email', value)}
                                error={Boolean(this.state.emailError)}
                                helperText={this.state.emailError}
                                fullWidth
                                autofocus
                                onKeyPress={this._onKeyPress}
                            />

                            <TextInput
                                id="password"
                                label={ i18n.t(tokens.labels.password) }
                                value={this.state.password}
                                inputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end" sx={{ marginRight: 1 }}>
                                            {
                                                this.state.visibility ?
                                                    <VisibilityIcon
                                                        style={{ cursor: "pointer", color: Theme.colors.darkGrey }}
                                                        onClick={this._toggleVisibility}
                                                    />
                                                    :
                                                    <VisibilityOffIcon
                                                        style={{ cursor: "pointer", color: Theme.colors.darkGrey }}
                                                        onClick={this._toggleVisibility}
                                                    />
                                            }
                                        </InputAdornment>
                                    )
                                }}
                                onBlur={() => this._onBlur('password')}
                                onChange={(value) => this._handleInputChange('password', value)}
                                error={Boolean(this.state.passwordError)}
                                helperText={this.state.passwordError}
                                secure={!this.state.visibility}
                                fullWidth
                                onKeyPress={this._onKeyPress}
                            />

                            <Button
                                onClick={this._login}
                                color="primary"
                                marginTop={ 3 }
                                fullWidth
                            >
                                { i18n.t(tokens.buttons.login) }
                            </Button>

                            <span style={ styles.subtext }>
                                <Button
                                    onClick={this._toggleForgotPassword}
                                    transparent
                                    customStyle={{ textTransform: "none", padding: 0, margin: 0, '&:hover': { backgroundColor: 'lightgrey' } }}
                                >
                                    <Text 
                                        small 
                                        avMedium 
                                        customStyle={{ 
                                            fontSize: "12px", 
                                            letterSpacing: "0.34284px" 
                                        }}>
                                        { i18n.t(tokens.labels.dashboard.forgotPassword) }
                                    </Text>

                                </Button>
                            </span>
                        </Form>
                }

                <Modal
                    open={this.state.forgotPassword}
                    onClose={this._toggleForgotPassword}
                    title={ i18n.t(tokens.titles.resetPassword) }
                    primaryButton={{
                        label: i18n.t(tokens.buttons.continue),
                        onClick: this._forgotPassword
                    }}
                    loader={this.props.loader}
                >
                    <Text medium light>
                        { i18n.t(tokens.labels.dashboard.resetPasswordConfirmation) }
                    </Text>

                </Modal>

                {this.props.loader?.isLoading && <Loader whitelabel={ whitelabel } />}
            </Container>
        );
    }
}

Login.propTypes = {
    loading: PropTypes.bool,
    whitelabel: PropTypes.object
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    loader: state.loader,
    whitelabel: state.whitelabel
});

export default connect(mapStateToProps)(Login);